/**
 * Inline SVG so the marketing pages stay first-party — no icon font, no CDN, nothing the content
 * security policy has to make room for. All drawn on a 24-grid with currentColor strokes.
 */

type IconProps = { size?: number };

function Svg({ size = 22, children }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function IconCalculator(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="4" y="2.5" width="16" height="19" rx="2.5" />
      <path d="M8 7h8M8 12h.01M12 12h.01M16 12h.01M8 16.5h.01M12 16.5h.01M16 16.5h.01" />
    </Svg>
  );
}

/** ДДС. A percent sign reads as a rate at 18px where a globe or a flag does not. */
export function IconPercent(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M19 5 5 19" />
      <circle cx="7.5" cy="7.5" r="2.8" />
      <circle cx="16.5" cy="16.5" r="2.8" />
    </Svg>
  );
}

/**
 * Declarations — a stack, because that is what they are, and because the single sheet was already
 * spoken for by invoices. Two destinations sharing one icon is a nav you navigate by reading.
 */
export function IconStack(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 2.8 21 7l-9 4.2L3 7z" />
      <path d="M3 12l9 4.2L21 12M3 17l9 4.2L21 17" />
    </Svg>
  );
}

/** A calendar grid, for the date field's trigger and anywhere a day is being picked. */
export function IconCalendarDays(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="4.8" width="18" height="16.2" rx="2.4" />
      <path d="M3 9.6h18M8 2.8v4M16 2.8v4" />
      <path d="M7.5 13.5h.01M12 13.5h.01M16.5 13.5h.01M7.5 17.3h.01M12 17.3h.01" />
    </Svg>
  );
}

export function IconDocument(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M14 2.5H7a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7.5z" />
      <path d="M14 2.5V7a1 1 0 0 0 1 1h4M8.5 13h7M8.5 17h4" />
    </Svg>
  );
}

export function IconBell(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M18 8.5a6 6 0 1 0-12 0c0 6-2.5 7.5-2.5 7.5h17S18 14.5 18 8.5" />
      <path d="M13.7 20a2 2 0 0 1-3.4 0" />
    </Svg>
  );
}

export function IconShield(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 2.5 4.5 5.8v5.4c0 4.6 3.2 8.9 7.5 10.3 4.3-1.4 7.5-5.7 7.5-10.3V5.8z" />
      <path d="m9 12 2.2 2.2L15.4 10" />
    </Svg>
  );
}

export function IconGlobe(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M2.5 12h19" />
      <path d="M12 2.5c2.5 2.6 3.9 6 3.9 9.5s-1.4 6.9-3.9 9.5c-2.5-2.6-3.9-6-3.9-9.5S9.5 5.1 12 2.5" />
    </Svg>
  );
}

export function IconCoins(props: IconProps) {
  return (
    <Svg {...props}>
      <ellipse cx="9" cy="6.5" rx="6" ry="3" />
      <path d="M3 6.5v5c0 1.7 2.7 3 6 3s6-1.3 6-3" />
      <path d="M3 11.5v5c0 1.7 2.7 3 6 3 1 0 2-.1 2.8-.3" />
      <circle cx="16.5" cy="15.5" r="5" />
      <path d="M16.5 13.2v4.6M14.8 14.6h3M14.8 16.6h3" />
    </Svg>
  );
}

export function IconArrow(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </Svg>
  );
}

export function IconCheck(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m4.5 12.5 5 5 10-11" />
    </Svg>
  );
}

export function IconLock(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="4" y="10.5" width="16" height="10.5" rx="2.5" />
      <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
    </Svg>
  );
}

export function IconQuestion(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9.25" />
      <path d="M9.4 9.2a2.7 2.7 0 1 1 3.35 3.06c-.55.16-.75.6-.75 1.14v.6" />
      <path d="M12 17.1h.01" />
    </Svg>
  );
}

export function IconClock(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 6.8V12l3.4 2" />
    </Svg>
  );
}

export function IconSun(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.3 5.3l1.55 1.55M17.15 17.15l1.55 1.55M5.3 18.7l1.55-1.55M17.15 6.85l1.55-1.55" />
    </Svg>
  );
}

export function IconMoon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M20.2 14.1A8.5 8.5 0 0 1 9.9 3.8a8.5 8.5 0 1 0 10.3 10.3Z" />
    </Svg>
  );
}

export function IconSignOut(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M9.5 20.5H6A2.5 2.5 0 0 1 3.5 18V6A2.5 2.5 0 0 1 6 3.5h3.5" />
      <path d="M15.5 16.5 20 12l-4.5-4.5M20 12H9.5" />
    </Svg>
  );
}

export function IconChevronDown(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m6 9.5 6 6 6-6" />
    </Svg>
  );
}
